<template>
  <div :id="id" ref="historyEl" class="chat-history">
    <slot name="header" />
    <div
      v-for="(message, index) in displayMessages"
      :key="index"
      class="chat-message"
      :class="{
        'user-message': message.role === 'user',
        'assistant-message': message.role === 'assistant',
      }"
    >
      <ConversationToolCalls
        v-if="message.role === 'assistant' && message.tool_calls?.length"
        :calls="message.tool_calls"
        :tool-call-state="
          index === inFlightAssistantMessageIndex
            ? inFlightToolCallState
            : undefined
        "
      />
      <div class="message-content">
        <Composer
          :value="message.content"
          :document="message.document ?? null"
          :workspace-id="workspaceId!"
          :reference-search-organization-id="referenceSearchOrganizationId"
          :reference-context-workspace-id="referenceContextWorkspaceId"
          :editable="false"
          :mentions-enabled="mentionsEnabled"
          :integration-organization-ids="integrationOrganizationIds"
        />
        <div
          v-if="message.role === 'user' && message.attachments?.length"
          class="message-attachments"
        >
          <div
            v-for="att in message.role === 'user' ? message.attachments : []"
            :key="att.id"
            class="message-attachment-chip"
          >
            <img
              v-if="att.mime.startsWith('image/')"
              :src="att.url"
              :alt="att.name"
              class="message-attachment-thumb"
            />
            <FileIcon v-else />
            <span class="message-attachment-name">{{
              att.name || "file"
            }}</span>
          </div>
        </div>
      </div>
      <div
        v-if="
          message.role === 'user' &&
          (messageIntegrationInfos[index]?.length > 0 ||
            messageReferenceInfos[index]?.length > 0)
        "
        class="message-integration-banner"
      >
        <div
          v-for="info in messageIntegrationInfos[index]"
          :key="integrationPillIdentity(info)"
          class="mib-row"
        >
          <img
            v-if="info.logoUri"
            :src="info.logoUri"
            :alt="info.name"
            class="mib-icon"
          />
          <span class="mib-name">{{ info.name }}</span>
          <span class="mib-label">integration added to context</span>
        </div>
        <div
          v-for="reference in messageReferenceInfos[index]"
          :key="reference.id"
          class="mib-row"
        >
          <ReferenceIcon
            :target="{ kind: reference.kind, id: reference.id }"
            class="mib-ref-icon"
          />
          <span class="mib-name">{{ reference.name }}</span>
          <span class="mib-label"
            >{{ referenceKindLabel(reference.kind) }} added to context</span
          >
        </div>
      </div>
      <div v-if="message.citations?.length" class="message-citations">
        <button
          v-for="citation in message.citations"
          :key="citation.url"
          class="citation-chip"
          @click="openCitation(citation.url)"
        >
          <span class="citation-chip-text">{{
            citation.title || citation.url
          }}</span>
          <ExternalArrowIcon class="citation-chip-icon" />
        </button>
      </div>
    </div>
    <slot name="footer" />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch, type PropType } from "vue";

import type { OrgId, TaskId, WorkspaceId } from "@ctrl/controller-rpc";

import {
  collectTodoTaskIds,
  parseRichContent,
  resolveTaskTitles,
  sanitizeAiDocument,
  type DisplayMessage,
} from "@/components/ai/conversationDocument";
import type {
  PendingMessage,
  ToolCallGroupState,
} from "@/components/ai/inFlightSends";
import {
  integrationPillIdentity,
  type IntegrationPill,
  type ReferenceInfo,
} from "@/components/ai/IntegrationPillsBar.vue";
import Composer from "@/components/editor/Composer.vue";
import ExternalArrowIcon from "@/components/icons/ExternalArrowIcon.vue";
import ReferenceIcon from "@/components/icons/ReferenceIcon.vue";
import * as inject from "@/components/inject";
import { referenceKindLabel } from "@/references/kinds";

import ConversationToolCalls from "./ConversationToolCalls.vue";
import FileIcon from "./FileIcon.vue";

export default defineComponent({
  name: "ConversationHistory",
  components: {
    ConversationToolCalls,
    Composer,
    ExternalArrowIcon,
    FileIcon,
    ReferenceIcon,
  },
  props: {
    // Optional id for the scrollable history root, e.g. the search scope of
    // a host's find bar.
    id: {
      type: String,
      default: undefined,
    },
    messages: {
      type: Array as PropType<PendingMessage[]>,
      required: true,
    },
    workspaceId: {
      type: String as PropType<WorkspaceId | undefined>,
      default: undefined,
    },
    mentionsEnabled: { type: Boolean, default: false },
    referenceSearchOrganizationId: {
      type: String as PropType<OrgId | null>,
      default: null,
    },
    referenceContextWorkspaceId: {
      type: String as PropType<WorkspaceId | null>,
      default: null,
    },
    integrationOrganizationIds: {
      type: Array as PropType<OrgId[] | undefined>,
      default: undefined,
    },
    messageIntegrationInfos: {
      type: Array as PropType<IntegrationPill[][]>,
      default: () => [],
    },
    messageReferenceInfos: {
      type: Array as PropType<ReferenceInfo[][]>,
      default: () => [],
    },
    inFlightToolCallState: {
      type: Object as PropType<ToolCallGroupState | undefined>,
      default: undefined,
    },
    inFlightAssistantMessageIndex: { type: Number, default: -1 },
  },
  setup(props) {
    const controller = inject.controller();
    // Task blocks in the (read-only) conversation documents are backed by
    // real tasks (see sanitizeAiDocument): allow opening and planning them.
    inject.readOnlyTaskActions(ref(true));
    const historyEl = ref<HTMLDivElement | null>(null);

    const richMessages = computed<DisplayMessage[]>(() =>
      props.messages.map((msg) => {
        if (msg.role !== "assistant" || ("document" in msg && msg.document))
          return msg;
        const rich = parseRichContent(msg.content) ?? undefined;
        return rich
          ? { ...msg, document: rich.document, citations: rich.citations }
          : msg;
      })
    );

    // AI documents may reference tasks that don't exist (hallucinated or
    // deleted ids). Resolve every referenced task id so the sanitizer below
    // can decide which `todo` blocks are backed by a real task. Until an id
    // is resolved it is treated as unbacked (fail closed).
    const taskTitles = ref(new Map<TaskId, string | null>());
    watch(
      () =>
        richMessages.value.flatMap((msg) =>
          msg.document ? collectTodoTaskIds(msg.document) : []
        ),
      async (ids) => {
        const unresolved = ids.filter((id) => !taskTitles.value.has(id));
        if (unresolved.length === 0) return;
        const resolved = await resolveTaskTitles(controller, unresolved);
        taskTitles.value = new Map([...taskTitles.value, ...resolved]);
      },
      { immediate: true }
    );

    const displayMessages = computed<DisplayMessage[]>(() =>
      richMessages.value.map((msg) =>
        msg.document
          ? {
              ...msg,
              document: sanitizeAiDocument(msg.document, (id) =>
                taskTitles.value.get(id)
              ),
            }
          : msg
      )
    );

    const openCitation = (url: string) => window.open(url, "_blank");

    return {
      historyEl,
      displayMessages,
      openCitation,
      integrationPillIdentity,
      referenceKindLabel,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/agent" as *;

.chat-history {
  @include agent-content-column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  padding-top: 24px;
  padding-bottom: 24px;
  user-select: text;
  -webkit-user-select: text;

  :deep(.composer-read-only),
  :deep(.composer-read-only .ProseMirror) {
    user-select: text;
    -webkit-user-select: text;
    pointer-events: auto;
  }

  :deep(.prosemirror-after-content) {
    display: none;
  }

  .message-integration-banner {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 6px;

    .mib-row {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .mib-icon {
      width: 14px;
      height: 14px;
      object-fit: contain;
      border-radius: $border-radius-s;
      flex-shrink: 0;
    }

    .mib-ref-icon {
      width: 14px;
      height: 14px;
      flex-shrink: 0;

      :deep(.fill) {
        @include themed() {
          fill: t($grey-50);
        }
      }
    }

    .mib-obj-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 14px;
      height: 14px;
      flex-shrink: 0;
      font-size: 12px;
      line-height: 1;
      @include themed() {
        color: t($grey-50);
      }
    }

    .mib-name {
      @extend %font-caption-medium;
      @include themed() {
        color: t($primary);
      }
    }

    .mib-label {
      @extend %font-caption-regular;
      @include themed() {
        color: t($grey-40);
      }
    }
  }

  .chat-message {
    display: flex;
    flex-direction: column;

    .message-content {
      border-radius: $border-radius-l;
      white-space: pre-wrap;
      word-wrap: break-word;

      .message-attachments {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 4px;

        .message-attachment-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          @extend %font-caption-regular;
          opacity: 0.7;
        }

        .message-attachment-thumb {
          width: 20px;
          height: 20px;
          object-fit: cover;
          border-radius: $border-radius-s;
          flex-shrink: 0;
        }

        .message-attachment-name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 160px;
        }
      }
    }

    &.user-message {
      align-items: flex-end;
      row-gap: 8px;

      .message-content {
        padding: 8px;
        max-width: 80%;
        @include themed() {
          background-color: t($grey-10);
          color: t($primary);
        }

        :deep(.ProseMirror) {
          @extend %font-body-regular;
        }
      }
    }

    &.assistant-message {
      align-items: flex-start;

      .message-content {
        @include themed() {
          color: t($primary);
        }
      }
    }

    .message-citations {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 8px;

      .citation-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 3px 8px;
        border-radius: $border-radius-m;
        font-size: 12px;
        max-width: 260px;
        cursor: pointer;
        font-family: inherit;
        background: none;
        @include themed() {
          background-color: t($grey-05);
          color: t($tertiary-50);
        }

        .citation-chip-text {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
        }

        .citation-chip-icon {
          flex-shrink: 0;
          width: 10px;
          height: 10px;
        }

        &:hover {
          text-decoration: underline;
          @include themed() {
            background-color: t($grey-15);
          }
        }
      }
    }
  }
}
</style>
